import { isSketchShape } from '../constants/nodeTypes';
import type { Node, Point, Size } from '../types';
import { isStrokeMarkup } from './markup';

export type StackSnap = 'center' | 'x' | 'y' | 'left' | 'right' | 'top' | 'bottom';

export const STACK_SNAP_THRESHOLD = 12;
export const STACK_SNAP_HOLD_EXTRA = 8;

export function isStackableDrawing(type: string): boolean {
  if (isStrokeMarkup(type)) return false;
  return isSketchShape(type) || type === 'text' || type === 'note';
}

export function getStackParentId(node: Pick<Node, 'data'>): string | undefined {
  return typeof node.data?.stackParentId === 'string' ? node.data.stackParentId : undefined;
}

const STACK_SNAPS: ReadonlySet<string> = new Set(['center', 'x', 'y', 'left', 'right', 'top', 'bottom']);

export function getStackSnap(node: Pick<Node, 'data'>): StackSnap | undefined {
  const value = node.data?.stackSnap;
  return typeof value === 'string' && STACK_SNAPS.has(value) ? value as StackSnap : undefined;
}

export function isDrawSnapModifier(evt: { ctrlKey?: boolean; metaKey?: boolean }): boolean {
  return Boolean(evt.ctrlKey || evt.metaKey);
}

/** Same modifier as live snap: hold Ctrl/Cmd to cascade inner resize to snapped outers. */
export const isDrawCascadeModifier = isDrawSnapModifier;

export function collectStackDescendantIds(parentId: string, nodes: Node[]): string[] {
  const ids: string[] = [];
  const visit = (id: string) => {
    for (const node of nodes) {
      if (getStackParentId(node) === id && !ids.includes(node.id)) {
        ids.push(node.id);
        visit(node.id);
      }
    }
  };
  visit(parentId);
  return ids;
}

export function nodeCenter(node: Pick<Node, 'position' | 'size'>): Point {
  return {
    x: node.position.x + node.size.width / 2,
    y: node.position.y + node.size.height / 2,
  };
}

function nodeContainsCenter(parent: Node, child: Node): boolean {
  const c = nodeCenter(child);
  return (
    c.x > parent.position.x
    && c.x < parent.position.x + parent.size.width
    && c.y > parent.position.y
    && c.y < parent.position.y + parent.size.height
  );
}

/**
 * Smallest stackable node that contains this node's center, excluding
 * stack descendants so a base never sticks to the shapes already on it.
 * Creation order is not a barrier: dropping an earlier shape onto a later
 * one is allowed, then z-order is fixed by `placeNodeAboveStackParent`.
 */
export function findStackParent(node: Node, nodes: Node[]): Node | null {
  if (!isStackableDrawing(node.type)) return null;
  const descendants = new Set(collectStackDescendantIds(node.id, nodes));
  const indexOf = new Map(nodes.map((candidate, index) => [candidate.id, index]));

  const containing = nodes.filter((candidate) => {
    if (candidate.id === node.id) return false;
    if (!isStackableDrawing(candidate.type)) return false;
    if (descendants.has(candidate.id)) return false;
    return nodeContainsCenter(candidate, node);
  });

  containing.sort((a, b) => {
    const area = (a.size.width * a.size.height) - (b.size.width * b.size.height);
    if (area !== 0) return area;
    return (indexOf.get(b.id) ?? 0) - (indexOf.get(a.id) ?? 0);
  });
  return containing[0] ?? null;
}

/**
 * Move the dropped node (and its own stickies) above the parent and the
 * parent's remaining stack so the just-placed drawing renders on top.
 */
export function placeNodeAboveStackParent(nodes: Node[], nodeId: string, parentId: string): Node[] {
  if (nodeId === parentId) return nodes;
  const movingSet = new Set([nodeId, ...collectStackDescendantIds(nodeId, nodes)]);
  const movingNodes = nodes.filter((node) => movingSet.has(node.id));
  const rest = nodes.filter((node) => !movingSet.has(node.id));
  const parentIndex = rest.findIndex((node) => node.id === parentId);
  if (parentIndex < 0 || movingNodes.length === 0) return nodes;

  const parentDesc = new Set(collectStackDescendantIds(parentId, rest));
  let insertAt = parentIndex + 1;
  for (let i = 0; i < rest.length; i++) {
    if (parentDesc.has(rest[i].id)) insertAt = Math.max(insertAt, i + 1);
  }
  return [...rest.slice(0, insertAt), ...movingNodes, ...rest.slice(insertAt)];
}

export function detectStackSnap(
  node: Node,
  parent: Node,
  held?: StackSnap,
  threshold: number = STACK_SNAP_THRESHOLD,
): StackSnap | undefined {
  const child = nodeCenter(node);
  const base = nodeCenter(parent);
  const xThresh = held === 'center' || held === 'x' ? threshold + STACK_SNAP_HOLD_EXTRA : threshold;
  const yThresh = held === 'center' || held === 'y' ? threshold + STACK_SNAP_HOLD_EXTRA : threshold;
  const xSnap = Math.abs(child.x - base.x) <= xThresh;
  const ySnap = Math.abs(child.y - base.y) <= yThresh;
  if (xSnap && ySnap) return 'center';
  if (xSnap) return 'x';
  if (ySnap) return 'y';
  return undefined;
}

function boxEdges(b: Pick<Node, 'position' | 'size'>) {
  const l = b.position.x;
  const t = b.position.y;
  const r = l + b.size.width;
  const btm = t + b.size.height;
  return { l, t, r, b: btm, cx: (l + r) / 2, cy: (t + btm) / 2, w: b.size.width, h: b.size.height };
}

export function snappedPosition(node: Pick<Node, 'position' | 'size'>, parent: Node, snap: StackSnap): Point {
  const p = boxEdges(parent);
  const { width, height } = node.size;
  switch (snap) {
    case 'center': return { x: p.cx - width / 2, y: p.cy - height / 2 };
    case 'x': return { x: p.cx - width / 2, y: node.position.y };
    case 'y': return { x: node.position.x, y: p.cy - height / 2 };
    case 'left': return { x: p.l, y: node.position.y };
    case 'right': return { x: p.r - width, y: node.position.y };
    case 'top': return { x: node.position.x, y: p.t };
    case 'bottom': return { x: node.position.x, y: p.b - height };
  }
}

export interface SnapGuide {
  axis: 'x' | 'y';
  at: number;
  start: number;
  end: number;
}

export interface DrawSnapHit {
  position: Point;
  snap?: StackSnap;
  guides: SnapGuide[];
}

type AxisHit = { pos: number; guideAt: number; kind: StackSnap | 'sibling'; dist: number };

function pickClosest(hits: AxisHit[]): AxisHit | undefined {
  if (hits.length === 0) return undefined;
  return hits.reduce((best, hit) => hit.dist < best.dist ? hit : best);
}

/** Nearby alignment for drawings: parent center/edges and sibling edges/centers. */
export function resolveDrawSnap(
  node: Node,
  nodes: Node[],
  opts?: { magnet?: boolean; threshold?: number; guideThreshold?: number },
): DrawSnapHit {
  const threshold = opts?.threshold ?? STACK_SNAP_THRESHOLD;
  const guideThreshold = opts?.guideThreshold ?? threshold + 8;
  const magnet = opts?.magnet ?? false;
  const parent = findStackParent(node, nodes);
  const self = boxEdges(node);
  const xHits: AxisHit[] = [];
  const yHits: AxisHit[] = [];

  const considerX = (pos: number, guideAt: number, kind: AxisHit['kind']) => {
    const dist = Math.abs(node.position.x - pos);
    if (dist <= guideThreshold) xHits.push({ pos, guideAt, kind, dist });
  };
  const considerY = (pos: number, guideAt: number, kind: AxisHit['kind']) => {
    const dist = Math.abs(node.position.y - pos);
    if (dist <= guideThreshold) yHits.push({ pos, guideAt, kind, dist });
  };

  let span = self;
  if (parent) {
    const p = boxEdges(parent);
    span = p;
    considerX(p.cx - self.w / 2, p.cx, 'x');
    considerX(p.l, p.l, 'left');
    considerX(p.r - self.w, p.r, 'right');
    considerY(p.cy - self.h / 2, p.cy, 'y');
    considerY(p.t, p.t, 'top');
    considerY(p.b - self.h, p.b, 'bottom');

    const siblings = nodes.filter((n) => {
      if (n.id === node.id || n.id === parent.id) return false;
      if (!isStackableDrawing(n.type)) return false;
      return getStackParentId(n) === parent.id || nodeContainsCenter(parent, n);
    });
    for (const sibling of siblings) {
      const e = boxEdges(sibling);
      considerX(e.l, e.l, 'sibling');
      considerX(e.r - self.w, e.r, 'sibling');
      considerX(e.cx - self.w / 2, e.cx, 'sibling');
      considerY(e.t, e.t, 'sibling');
      considerY(e.b - self.h, e.b, 'sibling');
      considerY(e.cy - self.h / 2, e.cy, 'sibling');
    }
  }

  const xHit = pickClosest(xHits.filter((h) => h.dist <= (magnet ? threshold : guideThreshold)));
  const yHit = pickClosest(yHits.filter((h) => h.dist <= (magnet ? threshold : guideThreshold)));
  const guides: SnapGuide[] = [];
  const xGuide = pickClosest(xHits);
  const yGuide = pickClosest(yHits);
  if (xGuide) guides.push({ axis: 'x', at: xGuide.guideAt, start: span.t - 12, end: span.b + 12 });
  if (yGuide) guides.push({ axis: 'y', at: yGuide.guideAt, start: span.l - 12, end: span.r + 12 });

  const magnetX = magnet && xHit && xHit.dist <= threshold ? xHit : undefined;
  const magnetY = magnet && yHit && yHit.dist <= threshold ? yHit : undefined;
  const position = {
    x: magnetX ? magnetX.pos : node.position.x,
    y: magnetY ? magnetY.pos : node.position.y,
  };

  let snap: StackSnap | undefined;
  const xKind = magnetX?.kind;
  const yKind = magnetY?.kind;
  if (xKind === 'x' && yKind === 'y') snap = 'center';
  else if (xKind === 'x') snap = 'x';
  else if (yKind === 'y') snap = 'y';
  else if (xKind === 'left' || xKind === 'right') snap = xKind;
  else if (yKind === 'top' || yKind === 'bottom') snap = yKind;

  return { position, snap, guides };
}

export function snappedResizeAxes(
  snap: StackSnap | undefined,
  fromCenter: boolean,
): { x: boolean; y: boolean } | null {
  if (fromCenter || snap === 'center') return { x: true, y: true };
  if (snap === 'x') return { x: true, y: false };
  if (snap === 'y') return { x: false, y: true };
  return null;
}

export function centeredResize(
  node: Pick<Node, 'position' | 'size'>,
  nextWidth: number,
  nextHeight: number,
  axes: { x: boolean; y: boolean },
): { position: Point; size: Size } {
  const width = axes.x ? nextWidth : node.size.width;
  const height = axes.y ? nextHeight : node.size.height;
  const cx = node.position.x + node.size.width / 2;
  const cy = node.position.y + node.size.height / 2;
  return {
    size: { width, height },
    position: {
      x: axes.x ? cx - width / 2 : node.position.x,
      y: axes.y ? cy - height / 2 : node.position.y,
    },
  };
}

export function symmetricHandleSize(
  origin: { w: number; h: number },
  rawW: number,
  rawH: number,
  axes: { x: boolean; y: boolean },
): Size {
  return {
    width: axes.x ? origin.w + (rawW - origin.w) * 2 : rawW,
    height: axes.y ? origin.h + (rawH - origin.h) * 2 : rawH,
  };
}

export interface NodeBoxPatch {
  id: string;
  position: Point;
  size: Size;
}

export function applyCascadeDelta(
  node: Pick<Node, 'position' | 'size'>,
  dW: number,
  dH: number,
  snap: StackSnap,
): { position: Point; size: Size } {
  const axes = snappedResizeAxes(snap, false) ?? { x: true, y: true };
  const nextW = axes.x ? Math.max(16, node.size.width + dW) : node.size.width;
  const nextH = axes.y ? Math.max(16, node.size.height + dH) : node.size.height;
  return centeredResize(node, nextW, nextH, axes);
}

/** Walk snapped parents outward so inner resize can keep concentric alignment. */
export function collectSnappedAncestorPatches(
  nodeId: string,
  dW: number,
  dH: number,
  nodes: Node[],
): NodeBoxPatch[] {
  if (dW === 0 && dH === 0) return [];
  const byId = new Map(nodes.map((node) => [node.id, node]));
  const patches: NodeBoxPatch[] = [];
  let child = byId.get(nodeId);
  const seen = new Set<string>([nodeId]);
  while (child) {
    const snap = getStackSnap(child);
    const parentId = getStackParentId(child);
    if (!snap || !parentId || seen.has(parentId)) break;
    if (snap !== 'center' && snap !== 'x' && snap !== 'y') break;
    const parent = byId.get(parentId);
    if (!parent) break;
    const next = applyCascadeDelta(parent, dW, dH, snap);
    patches.push({ id: parent.id, position: next.position, size: next.size });
    seen.add(parentId);
    child = { ...parent, position: next.position, size: next.size };
  }
  return patches;
}
